import { ReactNode, useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/utils/cn';
import { getStackZIndex, useModalStackDepth } from '@/contexts/ModalStackContext';
import { shouldYieldEscape } from '@/hooks/useEscapeLayer';

/**
 * Modal Component
 *
 * Standardized modal with:
 * - Fixed header with title and close button
 * - Scrollable content area
 * - Optional fixed footer
 * - Proper z-index layering
 * - Navbar-aware positioning
 * - Supports nested modals (nested prop increases z-index)
 */

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  className?: string;
  showCloseButton?: boolean;
  nested?: boolean; // Set to true for modals opened inside other modals
  raw?: boolean; // Set to true to render children without wrapper (for custom layouts)
  preventClose?: boolean; // Prevent closing the modal (e.g., during processing)
}

/** Exit animation length of the modal (Tailwind `animate-fade-out`), with a little slack. */
const MODAL_EXIT_MS = 300;

/** true while `open`, and for MODAL_EXIT_MS after it turns false (the DOM is still there, animating out). */
const useMountedThroughExit = (open: boolean): boolean => {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    const timer = window.setTimeout(() => setMounted(false), MODAL_EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [open]);
  return open || mounted;
};

export const Modal = ({
  open,
  onOpenChange,
  title,
  children,
  footer,
  size = 'md',
  className = '',
  showCloseButton = true,
  nested = false,
  raw = false,
  preventClose = false,
}: ModalProps) => {
  // Handle open change, respecting preventClose
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && preventClose) {
      return; // Don't allow closing
    }
    onOpenChange(newOpen);
  };

  // Register in the modal stack so this sits above anything already open
  // (e.g. a BottomSheet that spawned us). `nested` prop forces a minimum
  // depth of 2 for legacy callers that manually tag nested modals.
  // Stay registered through the exit animation so a closing modal does not dip below what is under it.
  const stackDepth = useModalStackDepth(useMountedThroughExit(open));
  const effectiveDepth = nested ? Math.max(stackDepth, 2) : stackDepth;
  const { backdrop: backdropZ, content: contentZ } = getStackZIndex(effectiveDepth);

  const sizeClasses: Record<NonNullable<ModalProps['size']>, string> = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    '2xl': 'max-w-6xl',
    full: 'max-w-7xl',
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        {/* Backdrop with flexbox centering - z-index comes from the modal stack */}
        <Dialog.Overlay
          style={{ zIndex: backdropZ }}
          className={cn(
            'fixed inset-0 bg-black/50 backdrop-blur-sm',
            'flex items-center justify-center',
            'data-[state=open]:animate-fade-in',
            'data-[state=closed]:animate-fade-out'
          )}
        >
          {/* Modal Content - Flexbox with fixed header/footer */}
          <Dialog.Content
            style={{ zIndex: contentZ }}
            onEscapeKeyDown={(e) => {
              // Radix listens in the capture phase, before an inner control can preventDefault. If the Esc belongs
              // to an open inline popup (expanded combobox / listbox / popper), let that popup close first.
              if (shouldYieldEscape(e)) {
                e.preventDefault();
              }
            }}
            className={cn(
              // Size
              'w-[calc(100vw-2rem)]',
              sizeClasses[size],

              // Smooth width transitions when size prop changes
              'transition-[max-width] duration-300 ease-out',

              // Appearance
              'bg-white rounded-2xl shadow-xl',

              // Layout - flex container for fixed header/footer pattern
              'flex flex-col',

              // Max height: account for navbar + bottom nav on mobile
              'max-h-[calc(100dvh-4.5rem-5rem)] lg:max-h-[calc(100dvh-clamp(2rem,5vh,8rem))]',
              // Short viewport (on-screen keyboard open): use the full visible height, footer never shrinks away
              'max-lg:[@media(max-height:600px)]:max-h-[calc(100dvh-1rem)]',

              // Shift up slightly on mobile to center between navbar and bottom nav
              '-mt-2 lg:mt-0',

              // Focus and animations
              'focus:outline-none',
              'data-[state=open]:animate-fade-in',
              'data-[state=closed]:animate-fade-out',

              className
            )}
          >
          {/* Accessible title & description (required for screen readers) */}
          {/* eslint-disable-next-line i18next/no-literal-string */}
          {/* When no visible title or in raw mode, hide these but keep for accessibility */}
          {(!title || raw) && (
            <VisuallyHidden>
              <Dialog.Title>{title || 'Modal'}</Dialog.Title>
              <Dialog.Description>
                {title ? `${title} dialog` : 'Modal dialog'}
              </Dialog.Description>
            </VisuallyHidden>
          )}

          {/* Raw mode: render children directly without wrapper */}
          {raw ? (
            children
          ) : (
            <>
              {/* Visible description (hidden) - only when title is shown */}
              {title && (
                <VisuallyHidden asChild>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <Dialog.Description>{title} dialog</Dialog.Description>
                </VisuallyHidden>
              )}

              {/* Fixed Header */}
              {title && (
                <div className="shrink-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between rounded-t-2xl gap-3 sm:gap-4">
                  <Dialog.Title className="text-base sm:text-lg md:text-xl font-bold text-gray-900 flex-1 min-w-0 leading-tight">
                    {title}
                  </Dialog.Title>
                  {showCloseButton && !preventClose && (
                    <Dialog.Close
                      className={cn(
                        'w-8 h-8 rounded-full',
                        'flex items-center justify-center',
                        'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
                        'transition-colors duration-200',
                        'focus:outline-none',
                        'shrink-0'
                      )}
                      aria-label="Close"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4L4 12M4 4L12 12"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Dialog.Close>
                  )}
                </div>
              )}

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 sm:py-6">
                {children}
              </div>

              {/* Fixed Footer (optional) */}
              {footer && (
                <div className="shrink-0 bg-white border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 rounded-b-2xl">
                  {footer}
                </div>
              )}
            </>
          )}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
